import { useContentfulImages } from '@snapchat/mw-contentful-client';
import { QuoteV2 } from '@snapchat/snap-design-system-marketing';
import { isNullish } from 'radashi';
import type { FC } from 'react';
import { useContext } from 'react';

import { AppContext } from '../../AppContext';
import type { ContentfulIdVariable } from '../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { isContentfulShallow } from '../../utils/contentful';
import { renderRichTextWithElementsNoHeadings } from '../../utils/renderText/renderRichText';
import type { CustomerQuoteDataProps, CustomerQuoteProps } from './query';
import { query } from './query';

export const Quote: FC<CustomerQuoteProps> = props => {
  const needsLoading = isContentfulShallow(props);
  const { getImageSources } = useContentfulImages();
  const { isRTL } = useContext(AppContext);

  const { data } = useContentfulQuery<CustomerQuoteDataProps, ContentfulIdVariable>(query, {
    variables: { id: props.sys.id },
    skip: !needsLoading,
  });

  if (needsLoading && !data) return null;

  const { author, authorName, body, logo, isLongQuote, media } = data?.customerQuote ?? props;

  // Get logo image source
  const logoImageSrc = logo?.media?.url ? getImageSources(logo.media.url) : undefined;

  // Get media sources
  const imgSrcs = media?.__typename === 'Image' ? getImageSources(media.media.url) : undefined;
  const videoSrc = media?.__typename === 'Video' ? media.media.url : undefined;
  const mobileVideoSrc = media?.__typename === 'Video' ? media.mobileMedia?.url : undefined;

  // Still fetching authorName from the older authorName field for backwards compatibility / fallback
  const authorText = isNullish(author) ? authorName : renderRichTextWithElementsNoHeadings(author);

  return (
    <QuoteV2
      author={authorText}
      logoImageSrc={logoImageSrc}
      isRtl={isRTL}
      isLongQuote={isLongQuote ?? false}
      imgSrcs={imgSrcs}
      videoSrc={videoSrc}
      mobileVideoSrc={mobileVideoSrc}
    >
      {renderRichTextWithElementsNoHeadings(body)}
    </QuoteV2>
  );
};
